import React, { useState, useEffect } from 'react';
import { Home, Save, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHomepage = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/homepage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching homepage content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API_URL}/api/admin/homepage`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving homepage content:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Homepage Editor</h1>
            <p className="text-gray-600">Edit the content displayed on your homepage</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchContent}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-blue-500 to-teal-500 text-white"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saved ? 'Saved!' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Hero Section */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5 text-blue-600" />
              Hero Section
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Main Title</label>
              <Input
                value={content.hero_title || ''}
                onChange={(e) => updateField('hero_title', e.target.value)}
                placeholder="Building Awesome Connections"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <Input
                value={content.hero_subtitle || ''}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                placeholder="One Adventure at a Time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={content.hero_description || ''}
                onChange={(e) => updateField('hero_description', e.target.value)}
                className="w-full border rounded-md p-2 min-h-[80px]"
                placeholder="Discover fun activities..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Text</label>
                <Input
                  value={content.hero_button_text || ''}
                  onChange={(e) => updateField('hero_button_text', e.target.value)}
                  placeholder="Get Started"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Link</label>
                <Input
                  value={content.hero_button_link || ''}
                  onChange={(e) => updateField('hero_button_link', e.target.value)}
                  placeholder="/activities"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Section */}
        <Card className="border-2 border-green-200">
          <CardHeader className="bg-green-50">
            <CardTitle>Stats Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Activities Count</label>
                <Input
                  value={content.stats_activities || ''}
                  onChange={(e) => updateField('stats_activities', e.target.value)}
                  placeholder="50+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Activities Label</label>
                <Input
                  value={content.stats_activities_label || ''}
                  onChange={(e) => updateField('stats_activities_label', e.target.value)}
                  placeholder="Fun Activities"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Resources Count</label>
                <Input
                  value={content.stats_resources || ''}
                  onChange={(e) => updateField('stats_resources', e.target.value)}
                  placeholder="12+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Resources Label</label>
                <Input
                  value={content.stats_resources_label || ''}
                  onChange={(e) => updateField('stats_resources_label', e.target.value)}
                  placeholder="Expert Resources"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Community Count</label>
                <Input
                  value={content.stats_community || ''}
                  onChange={(e) => updateField('stats_community', e.target.value)}
                  placeholder="1000+"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Community Label</label>
                <Input
                  value={content.stats_community_label || ''}
                  onChange={(e) => updateField('stats_community_label', e.target.value)}
                  placeholder="Happy Dads"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features Section */}
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-50">
            <CardTitle>Features Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Section Title</label>
                <Input
                  value={content.features_title || ''}
                  onChange={(e) => updateField('features_title', e.target.value)}
                  placeholder="Everything You Need"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Section Subtitle</label>
                <Input
                  value={content.features_subtitle || ''}
                  onChange={(e) => updateField('features_subtitle', e.target.value)}
                  placeholder="to Be an Amazing Dad"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <p className="font-medium text-sm text-gray-500">Feature {num}</p>
                  <Input
                    value={content[`feature_${num}_title`] || ''}
                    onChange={(e) => updateField(`feature_${num}_title`, e.target.value)}
                    placeholder="Feature Title"
                  />
                  <textarea
                    value={content[`feature_${num}_description`] || ''}
                    onChange={(e) => updateField(`feature_${num}_description`, e.target.value)}
                    className="w-full border rounded-md p-2 text-sm"
                    placeholder="Feature description..."
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="border-2 border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle>Call to Action Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">CTA Title</label>
              <Input
                value={content.cta_title || ''}
                onChange={(e) => updateField('cta_title', e.target.value)}
                placeholder="Ready to Be the Dad You've Always Wanted to Be?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CTA Description</label>
              <textarea
                value={content.cta_description || ''}
                onChange={(e) => updateField('cta_description', e.target.value)}
                className="w-full border rounded-md p-2"
                placeholder="Join thousands of fathers..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Button Text</label>
                <Input
                  value={content.cta_button_text || ''}
                  onChange={(e) => updateField('cta_button_text', e.target.value)}
                  placeholder="Start Your Journey"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Link</label>
                <Input
                  value={content.cta_button_link || ''}
                  onChange={(e) => updateField('cta_button_link', e.target.value)}
                  placeholder="/signup"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Newsletter Section */}
        <Card className="border-2 border-teal-200">
          <CardHeader className="bg-teal-50">
            <CardTitle>Newsletter Section</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Newsletter Title</label>
              <Input
                value={content.newsletter_title || ''}
                onChange={(e) => updateField('newsletter_title', e.target.value)}
                placeholder="Stay Connected"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Newsletter Description</label>
              <textarea
                value={content.newsletter_description || ''}
                onChange={(e) => updateField('newsletter_description', e.target.value)}
                className="w-full border rounded-md p-2"
                placeholder="Get monthly tips..."
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Button Text</label>
              <Input
                value={content.newsletter_button_text || ''}
                onChange={(e) => updateField('newsletter_button_text', e.target.value)}
                placeholder="Subscribe"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button (bottom) */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-teal-500 text-white"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saved ? 'Saved!' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminHomepage;
